import React, { useRef, useEffect } from "react";

const LivingVineBackground = ({
  children,
  vineColor = "rgba(255, 255, 255, 0.85)",
  branchColor = "rgba(255, 255, 255, 0.55)",
  maxBranchLength = 90,
  className = "",
}) => {
  const canvasRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const targetPosRef = useRef({ x: 0, y: 0 });
  const pathHistoryRef = useRef([]);
  const branchesRef = useRef([]);
  const lastMouseMoveRef = useRef(Date.now());
  const timeRef = useRef(0);

  useEffect(() => {
    let destroyed = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      mousePosRef.current = {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };
      targetPosRef.current = { ...mousePosRef.current };
    };

    setCanvasSize();

    class Branch {
      constructor(x, y) {
        this.points = [{ x, y }];
        this.life = 1;
        this.angle = Math.random() * Math.PI * 2;
        this.speed = Math.random() * 1.4 + 0.4;
        this.length = 0;
        this.colorAlpha = Math.random() * 0.35 + 0.15;
      }

      update() {
        if (this.length >= maxBranchLength) {
          this.life -= 0.018;
          return;
        }

        this.angle += (Math.random() - 0.5) * 0.22;

        const last = this.points[this.points.length - 1];
        const newX = last.x + Math.cos(this.angle) * this.speed;
        const newY = last.y + Math.sin(this.angle) * this.speed;

        this.points.push({ x: newX, y: newY });
        this.length++;
      }

      draw() {
        if (this.points.length < 2) return;

        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);

        for (let i = 1; i < this.points.length; i++) {
          ctx.lineTo(this.points[i].x, this.points[i].y);
        }

        ctx.strokeStyle = `rgba(255, 255, 255, ${this.life * this.colorAlpha})`;
        ctx.lineWidth = 1.4;
        ctx.shadowBlur = 14;
        ctx.shadowColor = branchColor;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }

    const handleMouseMove = (e) => {
      lastMouseMoveRef.current = Date.now();
      mousePosRef.current = { x: e.clientX, y: e.clientY };
      targetPosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleTouchMove = (e) => {
      lastMouseMoveRef.current = Date.now();
      const touch = e.touches[0];
      if (!touch) return;
      mousePosRef.current = { x: touch.clientX, y: touch.clientY };
      targetPosRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleResize = () => {
      setCanvasSize();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("resize", handleResize);

    const animate = () => {
      if (destroyed) return;

      timeRef.current += 0.012;
      const t = timeRef.current;

      const width = canvas.width;
      const height = canvas.height;

      const idleFor = Date.now() - lastMouseMoveRef.current;
      const isIdle = idleFor > 1200;

      if (isIdle) {
        // Automatic motion when the user is not moving the mouse
        const cx = width * 0.5;
        const cy = height * 0.5;

        const driftX =
          Math.sin(t * 1.2) * width * 0.28 +
          Math.sin(t * 0.37) * width * 0.12;

        const driftY =
          Math.cos(t * 1.05) * height * 0.22 +
          Math.sin(t * 0.52) * height * 0.08;

        targetPosRef.current = {
          x: cx + driftX,
          y: cy + driftY,
        };
      }

      // Smooth follow so the line feels alive
      mousePosRef.current.x +=
        (targetPosRef.current.x - mousePosRef.current.x) * 0.03;
      mousePosRef.current.y +=
        (targetPosRef.current.y - mousePosRef.current.y) * 0.03;

      // Fade slowly so trails remain visible
      ctx.fillStyle = "rgba(0, 0, 0, 0.090)";
      ctx.fillRect(0, 0, width, height);

      // Main moving line trail
      pathHistoryRef.current.push({ ...mousePosRef.current });
      if (pathHistoryRef.current.length > 160) {
        pathHistoryRef.current.shift();
      }

      if (pathHistoryRef.current.length > 1) {
        ctx.beginPath();
        ctx.moveTo(pathHistoryRef.current[0].x, pathHistoryRef.current[0].y);

        for (let i = 1; i < pathHistoryRef.current.length; i++) {
          ctx.lineTo(pathHistoryRef.current[i].x, pathHistoryRef.current[i].y);
        }

        ctx.strokeStyle = vineColor;
        ctx.lineWidth = 2.2;
        ctx.shadowBlur = 20;
        ctx.shadowColor = vineColor;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Spawn branches even without mouse movement
      if (Math.random() > 0.94) {
        branchesRef.current.push(
          new Branch(mousePosRef.current.x, mousePosRef.current.y)
        );
      }

      branchesRef.current = branchesRef.current.filter((b) => b.life > 0);
      for (const branch of branchesRef.current) {
        branch.update();
        branch.draw();
      }

      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      destroyed = true;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, [vineColor, branchColor, maxBranchLength]);

  return (
    <div
      className={`relative h-screen w-screen overflow-hidden bg-black ${className}`}
      style={{ backgroundColor: "#000000" }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block h-full w-full z-0"
        style={{ pointerEvents: 'none' }}
      />
      <div className="relative z-10 h-full w-full">{children}</div>
    </div>
  );
};

export default LivingVineBackground;